import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { RootState } from '../app/redux/getReduxStore';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated';
import type { DocumentType } from '../graphql/generated';
import { UserType } from '../graphql/generated/graphql';
import useDragDropRef from '../utils/useDragDropRef';
import * as styles from './ContentMapColumn.module.scss';
import ContentMapColumnBreadcrumbs from './ContentMapColumnBreadcrumbs';
import { DragTypes, MoveStates } from './constants';
import topicPolicyCheck, { TopicPolicyAction } from './topicPolicyCheck';
import TopicTreeCard, { getCardHeight } from './treeCard/TopicTreeCard';
import type { TopicTreeCardCallbacks } from './treeCard/TopicTreeCard';
import type {
  SelectedPosts,
  TopicForReorganization,
  TopicReorganizationMetadata,
  TopicReorganizationTree,
} from './useTopicReorganization';
import type { ContentMapGetDropParams } from './utils/types';
import useStayInViewport from './utils/useStayInViewport';

const POST_FRAGMENT = gql(`
  fragment ContentMapColumn_postsMap on Post {
    id
    ...TopicTreeCard_postsMap
  }
`);

const getTopicIdFromTreeChildren = (tree: TopicReorganizationTree): string[] =>
  tree.children.flatMap((child) => [
    child.topic.id,
    ...getTopicIdFromTreeChildren(child),
  ]);

const IS_TOPIC_NODE_REORGANIZABLE_TOPIC_FRAGMENT = gql(`
  fragment isTopicNodeReorganizable_topic on Topic {
    id
    ...topicPolicyCheck_topic
  }
`);

interface IsTopicNodeReorganizableArgs {
  topic: DocumentType<typeof IS_TOPIC_NODE_REORGANIZABLE_TOPIC_FRAGMENT>;
  reorganizationMetadata: TopicReorganizationMetadata;
}

const isTopicNodeReorganizable = (
  { topic, reorganizationMetadata }: IsTopicNodeReorganizableArgs,
  user: RootState['session']['user'],
) =>
  user.type === UserType.Admin ||
  reorganizationMetadata.isNew ||
  topicPolicyCheck(TopicPolicyAction.ReorganizeTopic, topic, user);

const COLUMN_WIDTH = parseInt(styles.columnWidth, 10);
export const SPECIAL_COLUMN_PADDING = parseInt(styles.specialColumnPadding, 10);

interface ContentMapColumnProps
  extends Omit<TopicTreeCardCallbacks, 'onExpandedChange'> {
  tree: TopicReorganizationTree;
  specialColumn: 'posts' | 'unorganized' | null;
  showPosts: boolean;
  selectedPosts: SelectedPosts;
  allExpanded: boolean;
  isReorganizing: boolean;
  getDropParams: ContentMapGetDropParams;
  loadPostEdges: (topicId: string) => void;
  postsMap: Record<string, DocumentType<typeof POST_FRAGMENT>>;
  loadPost: (postId: string) => void;
}

const ContentMapColumn = ({
  tree,
  specialColumn,
  isReorganizing,
  showPosts,
  selectedPosts,
  getDropParams,
  allExpanded,
  onPostSelectedChange,
  onPostDeletedChange,
  onPostPinnedChange,
  onPostArchivedChange,
  onTopicSelectedChange,
  onTopicDeletedChange,
  onTopicNameChange,
  onSubtopicCreate,
  loadPostEdges,
  postsMap,
  loadPost,
}: ContentMapColumnProps) => {
  const [height, setHeight] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([tree.topic]);
  const [expandedByTopicId, setExpandedByTopicId] = useState<
    Record<string, boolean>
  >({});
  const [dragOverData, setDragOverData] = useState<
    { topicId: string; key: string } | { topicId?: never; key?: never }
  >({});
  const isDragOver = dragOverData.topicId === tree.topic.id;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollParentRef = useRef<HTMLDivElement | null>(null);
  const listInnerRef = useRef<HTMLDivElement | null>(null);

  const getPropsForStayInViewport = useStayInViewport((ids) => {
    ids.forEach((id) => loadPostEdges(id));
  });

  const currentUser = useAppSelector((state) => state.session.user);
  const isRootTopicDraggable = isTopicNodeReorganizable(tree, currentUser);

  const dragSpec = useMemo(
    () => ({
      type: DragTypes.column,
      item: {
        type: DragTypes.column,
        toMove: {
          postKeys: [],
          pinnedPostKeys: [],
          topicIds: [tree.topic.id],
        },
      },
      canDrag: isReorganizing && isRootTopicDraggable && !specialColumn,
      collect: () => null,
    }),
    [isReorganizing, isRootTopicDraggable, tree.topic, specialColumn],
  );
  const [, originalDragRef, preview] = useDrag(dragSpec);
  const dragRef = useDragDropRef(originalDragRef);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const isExpanded = useCallback(
    (topicId: string) => {
      if (Object.prototype.hasOwnProperty.call(expandedByTopicId, topicId)) {
        return expandedByTopicId[topicId] as boolean;
      }
      return allExpanded;
    },
    [expandedByTopicId, allExpanded],
  );

  useEffect(() => {
    setExpandedByTopicId({});
  }, [allExpanded]);

  const handleDragOverChange = useCallback(
    (over: boolean, data: { topicId: string; key: string }) =>
      setDragOverData((prev) => {
        if (over) {
          return data;
        }
        if (!over && prev.key === data.key) {
          return {};
        }
        return prev;
      }),
    [],
  );

  const rows = useMemo(() => {
    const commonProps = {
      showPosts,
      getDropParams,
      isReorganizing,
      columnSelected: tree.selected,
      dragOverTopicId: dragOverData.topicId,
      onPostSelectedChange,
      onPostDeletedChange,
      onPostPinnedChange,
      onPostArchivedChange,
      onTopicSelectedChange,
      onTopicDeletedChange,
      onTopicNameChange,
      onSubtopicCreate,
      onDragOverChange: handleDragOverChange,
    };

    const result: {
      topic: TopicForReorganization;
      key: string;
      render: (style: CSSProperties) => ReactNode;
      height: number;
    }[] = [];

    const addTreeToResult = (
      t: TopicReorganizationTree,
      ancestorsReorganizationMetadata: TopicReorganizationMetadata[] = [],
    ) => {
      const { topic, reorganizationMetadata } = t;
      const isHeader = t === tree;
      const shouldExpand = isHeader || isExpanded(topic.id);

      let postCountForCardHeight = 0;
      if (showPosts && shouldExpand) {
        postCountForCardHeight =
          t.reorganizationTopicPosts.status === 'loaded'
            ? t.reorganizationTopicPosts.edges.length
            : t.reorganizationTopicPosts.postIds.length;
      }

      result.push({
        topic,
        key: `${topic.id}-${reorganizationMetadata.move.state}`,
        render: (style) => (
          <div
            className={styles.itemContainer}
            style={style}
            {...getPropsForStayInViewport(topic.id)}
          >
            <TopicTreeCard
              header={isHeader}
              specialColumn={specialColumn}
              tree={t}
              reorganizable={isTopicNodeReorganizable(t, currentUser)}
              ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
              selectedPosts={selectedPosts}
              expanded={shouldExpand}
              onExpandedChange={(recursive) => {
                const changes = { [topic.id]: !shouldExpand };
                if (recursive) {
                  getTopicIdFromTreeChildren(t).forEach((topicId) => {
                    changes[topicId] = !shouldExpand;
                  });
                }
                setExpandedByTopicId((prev) => ({ ...prev, ...changes }));
              }}
              postsMap={postsMap}
              loadPost={loadPost}
              {...commonProps}
            />
          </div>
        ),
        height: getCardHeight(postCountForCardHeight),
      });

      if (shouldExpand) {
        t.children.forEach((subtree) => {
          addTreeToResult(subtree, [
            ...ancestorsReorganizationMetadata,
            reorganizationMetadata,
          ]);
        });
      }
    };

    addTreeToResult(tree);

    return result;
  }, [
    isExpanded,
    getDropParams,
    isReorganizing,
    showPosts,
    tree,
    specialColumn,
    onPostSelectedChange,
    onPostDeletedChange,
    onPostPinnedChange,
    onPostArchivedChange,
    onTopicSelectedChange,
    onTopicDeletedChange,
    onTopicNameChange,
    onSubtopicCreate,
    handleDragOverChange,
    dragOverData,
    currentUser,
    selectedPosts,
    getPropsForStayInViewport,
    postsMap,
    loadPost,
  ]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (containerRef.current) {
          setHeight(containerRef.current.getBoundingClientRect().height);
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, []);

  useLayoutEffect(() => {
    if (listInnerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (listInnerRef.current) {
          setWidth(listInnerRef.current.getBoundingClientRect().width);
        }
      });
      resizeObserver.observe(listInnerRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [height]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: (index) => rows[index]!.height,
    getItemKey: (index) => rows[index]!.key,
    onChange(instance) {
      if (!instance.range) return;

      const invisibleRows = rows
        .slice(0, instance.range.startIndex + 1)
        .map(({ topic }) => topic)
        .filter(Boolean);

      const currentTopic = _.last(invisibleRows);
      if (currentTopic) {
        const ancestors = _.compact(
          (currentTopic?.ancestors ?? []).map(({ id }) =>
            invisibleRows.find((topic) => topic.id === id),
          ),
        ).toReversed();

        setBreadcrumbs([...ancestors, currentTopic]);
      }
    },
  });

  return (
    <div
      ref={dragRef}
      className={classNames({
        [styles.draggable]: isReorganizing && isRootTopicDraggable,
      })}
    >
      <div
        className={classNames(
          styles.container,
          specialColumn && styles[specialColumn],
        )}
        ref={containerRef}
      >
        {typeof height === 'number' && (
          <div
            ref={scrollParentRef}
            className={styles.scrollParent}
            style={{ height, width: COLUMN_WIDTH }}
            onScroll={() => {
              const scrollTop = scrollParentRef.current?.scrollTop;
              const isNewHeaderStuck = scrollTop != null && scrollTop > 0;
              if (isNewHeaderStuck !== isHeaderStuck) {
                setIsHeaderStuck(isNewHeaderStuck);
              }
            }}
          >
            <div
              ref={listInnerRef}
              className={styles.listInner}
              style={{ height: rowVirtualizer.getTotalSize() }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const { index } = virtualRow;
                const style: CSSProperties = {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                  height: virtualRow.size,
                };
                return (
                  <React.Fragment key={virtualRow.key}>
                    {rows[index]!.render(style)}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
        {typeof width === 'number' && (
          <div
            style={{ width }}
            className={classNames(styles.header, {
              [styles.stuck]: isHeaderStuck,
            })}
          >
            <div
              className={classNames(styles.drop, {
                [styles.isDragOver]: isDragOver,
              })}
            >
              <div className={styles.headerContent}>
                <ContentMapColumnBreadcrumbs
                  movedIn={
                    tree.reorganizationMetadata.move.state ===
                    MoveStates.movedIn
                  }
                  link={!isReorganizing && !specialColumn}
                  breadcrumbs={breadcrumbs}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ContentMapColumn);
